import { useState, useEffect } from 'react';
import { useWellness } from '../context/WellnessContext';
import { useTranslation } from '../context/LanguageContext';
import { Dialog, DialogContent } from './ui/dialog';
import { Button } from './ui/button';
import { Coffee, Wind, X, Play, Pause, SkipForward } from 'lucide-react';

const BreakSuggestionModal = () => {
  const {
    showBreakSuggestion, breakMessage, dismissBreakSuggestion, startBreak,
    totalSessionTime, getRandomBreathingExercise
  } = useWellness();
  const { t } = useTranslation();
  const [exercise] = useState(getRandomBreathingExercise);
  if (!showBreakSuggestion) return null;

  return (
    <Dialog open={showBreakSuggestion} onOpenChange={dismissBreakSuggestion}>
      <DialogContent className="sm:max-w-md rounded-3xl overflow-hidden p-0 z-[200]" aria-describedby="break-description">
        <div className="relative h-32 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-[var(--papo-red)] via-[var(--papo-deep-red)] to-[var(--papo-gold)] animate-gradient"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-20 h-20 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center animate-pulse">
              <Coffee className="w-10 h-10 text-white" />
            </div>
          </div>
          <button onClick={dismissBreakSuggestion} className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 text-center">
          <h2 className="text-2xl font-bold text-[var(--text-primary)] mb-3 font-['Nunito']">{t('wellness.breakTitle')}</h2>
          <p id="break-description" className="text-[var(--text-secondary)] mb-6 leading-relaxed">{breakMessage}</p>
          <div className="bg-[var(--elevated-bg)] rounded-2xl p-4 mb-6">
            <p className="text-sm text-[var(--text-secondary)]">{t('wellness.sessionTime')}: <span className="font-semibold text-[var(--text-primary)]">{totalSessionTime} {t('wellness.minutes')}</span></p>
          </div>
          <div className="bg-[var(--papo-red)]/10 rounded-2xl p-4 mb-6">
            <div className="flex items-center gap-2 mb-2">
              <Wind className="w-5 h-5 text-[var(--papo-gold)]" />
              <span className="font-semibold text-[var(--text-primary)]">{exercise.name}</span>
            </div>
            <p className="text-sm text-[var(--text-secondary)]">{t('wellness.inhale')} {exercise.inhale}s - {t('wellness.hold')} {exercise.hold}s - {t('wellness.exhale')} {exercise.exhale}s</p>
          </div>
          <div className="flex gap-3">
            <Button onClick={dismissBreakSuggestion} variant="outline" className="flex-1 rounded-full border-white/10" data-testid="dismiss-break-btn">
              <SkipForward className="w-4 h-4 mr-2" />{t('wellness.notNow')}
            </Button>
            <Button onClick={() => startBreak(5)} className="flex-1 rounded-full gradient-warm text-white font-bold" data-testid="start-break-btn">
              <Play className="w-4 h-4 mr-2" />{t('wellness.break5')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const BreakModeOverlay = () => {
  const { isOnBreak, breakTimeRemaining, endBreak, getRandomBreathingExercise } = useWellness();
  const { t } = useTranslation();
  const [exercise] = useState(getRandomBreathingExercise);
  const [breathPhase, setBreathPhase] = useState('inhale');
  const [phaseTime, setPhaseTime] = useState(exercise.inhale);

  useEffect(() => {
    if (!isOnBreak) return;
    const interval = setInterval(() => {
      setPhaseTime(prev => {
        if (prev <= 1) {
          if (breathPhase === 'inhale') { setBreathPhase('hold'); return exercise.hold; }
          else if (breathPhase === 'hold') { setBreathPhase('exhale'); return exercise.exhale; }
          else { setBreathPhase('inhale'); return exercise.inhale; }
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [isOnBreak, breathPhase, exercise]);

  if (!isOnBreak) return null;

  const formatTime = (seconds) => { const mins = Math.floor(seconds / 60); const secs = seconds % 60; return `${mins}:${secs.toString().padStart(2, '0')}`; };
  const phaseLabels = { inhale: `${t('wellness.inhale')}...`, hold: `${t('wellness.hold')}...`, exhale: `${t('wellness.exhale')}...` };
  const phaseColors = { inhale: 'from-[var(--papo-red)] to-[var(--papo-gold)]', hold: 'from-[var(--papo-deep-red)] to-[var(--papo-red)]', exhale: 'from-[var(--papo-gold)] to-[var(--papo-red)]' };

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center">
      <div className={`absolute inset-0 bg-gradient-to-br ${phaseColors[breathPhase]} opacity-90 transition-all duration-1000`} />
      <div className="relative text-center text-white">
        <div className="relative mb-8">
          <div className={`w-48 h-48 mx-auto rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center transition-all duration-1000 ${breathPhase === 'inhale' ? 'scale-110' : breathPhase === 'exhale' ? 'scale-90' : 'scale-100'}`}>
            <div className="text-center"><p className="text-3xl font-bold mb-1">{phaseTime}</p><p className="text-lg opacity-80">{phaseLabels[breathPhase]}</p></div>
          </div>
          <div className={`absolute inset-0 w-48 h-48 mx-auto rounded-full border-4 border-white/30 transition-all duration-1000 ${breathPhase === 'inhale' ? 'scale-125 opacity-0' : 'scale-100 opacity-100'}`} />
        </div>
        <h2 className="text-3xl font-black mb-2 font-['Nunito']">{t('wellness.restMode')}</h2>
        <p className="text-xl opacity-90 mb-2">{exercise.name}</p>
        <p className="text-4xl font-bold mb-8">{formatTime(breakTimeRemaining)}</p>
        <Button onClick={endBreak} variant="outline" className="rounded-full bg-white/20 border-white/30 text-white hover:bg-white/30" data-testid="end-break-btn">
          <Pause className="w-4 h-4 mr-2" />{t('wellness.endBreak')}
        </Button>
      </div>
    </div>
  );
};

const WellnessWidget = () => {
  const { totalSessionTime, dailyUsage, settings, startBreak } = useWellness();
  const { t } = useTranslation();
  const dailyPercentage = Math.min((dailyUsage / settings.dailyLimit) * 100, 100);
  const isNearLimit = dailyPercentage >= 80;

  return (
    <div className="bg-[var(--card-bg)] rounded-3xl p-6 border border-white/5 card-shadow" data-tour="wellness" data-testid="wellness-widget">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-full gradient-nature flex items-center justify-center"><Wind className="w-5 h-5 text-white" /></div>
        <div>
          <h3 className="font-bold text-[var(--text-primary)]">{t('wellness.title')}</h3>
          <p className="text-xs text-[var(--text-secondary)]">{t('wellness.session')}: {totalSessionTime} {t('common.min')}</p>
        </div>
      </div>
      <div className="mb-4">
        <div className="flex justify-between text-sm mb-1">
          <span className="text-[var(--text-secondary)]">{t('wellness.dailyUsage')}</span>
          <span className={isNearLimit ? 'text-[var(--papo-red)] font-semibold' : 'text-[var(--text-secondary)]'}>{dailyUsage}/{settings.dailyLimit} {t('common.min')}</span>
        </div>
        <div className="h-2 bg-[var(--elevated-bg)] rounded-full overflow-hidden">
          <div className={`h-full rounded-full transition-all duration-500 ${isNearLimit ? 'bg-gradient-to-r from-[var(--papo-gold)] to-[var(--papo-red)]' : 'gradient-nature'}`} style={{ width: `${dailyPercentage}%` }} />
        </div>
      </div>
      <p className="text-sm text-[var(--text-secondary)] mb-4 leading-relaxed">
        {isNearLimit ? t('wellness.nearLimit') : t('wellness.reminder')}
      </p>
      <Button onClick={() => startBreak(5)} variant="outline" className="w-full rounded-full border-[var(--papo-gold)] text-[var(--papo-gold)] hover:bg-[var(--papo-gold)]/10" data-testid="manual-break-btn">
        <Coffee className="w-4 h-4 mr-2" />{t('wellness.takeBreak')}
      </Button>
    </div>
  );
};

export { BreakSuggestionModal, BreakModeOverlay, WellnessWidget };
